import React, { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, MailCheck, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../lib/api";
import { CodeInput } from "../../components/CodeInput";

export default function ForgotPasswordPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isAuthenticated, loading } = useAuth();
    const [step, setStep] = useState("email"); // "email" | "code" | "newpwd"
    const [email, setEmail] = useState("");
    const [code, setCode] = useState("");
    const [resetToken, setResetToken] = useState("");
    const [pwd, setPwd] = useState("");
    const [pwd2, setPwd2] = useState("");
    const [show, setShow] = useState(false);
    const [busy, setBusy] = useState(false);

    if (!loading && isAuthenticated) return <Navigate to="/bookings" replace />;

    const submitEmail = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/api/auth/password-reset/initiate", { email });
            setStep("code");
            toast.success(t("auth.code_sent"));
        } catch (err) {
            const status = err?.response?.status;
            if (status === 404) toast.error(t("auth.no_account_with_email"));
            else if (status === 429) toast.error(t("auth.too_many_requests"));
            else if (status === 422) toast.error(t("auth.invalid_email_format"));
            else toast.error(t("common.error"));
        } finally {
            setBusy(false);
        }
    };

    const submitCode = async (e) => {
        e.preventDefault();
        if (code.length !== 6) return;
        setBusy(true);
        try {
            const { data } = await api.post("/api/auth/password-reset/verify", { email, code });
            setResetToken(data.reset_token);
            setStep("newpwd");
        } catch (err) {
            const detail = err?.response?.data?.detail || "";
            if (detail.toLowerCase().includes("invalid code")) toast.error(t("auth.invalid_code"));
            else if (detail.toLowerCase().includes("expired")) toast.error(t("auth.code_expired"));
            else if (detail.toLowerCase().includes("attempts")) toast.error(t("auth.too_many_attempts"));
            else toast.error(t("common.error"));
        } finally {
            setBusy(false);
        }
    };

    const submitNewPwd = async (e) => {
        e.preventDefault();
        if (pwd.length < 6) return toast.error(t("auth.password_too_short"));
        if (pwd !== pwd2) return toast.error(t("auth.passwords_do_not_match"));
        setBusy(true);
        try {
            await api.post("/api/auth/password-reset/confirm", { reset_token: resetToken, new_password: pwd });
            toast.success(t("auth.password_changed"));
            navigate("/login", { replace: true });
        } catch (err) {
            const detail = (err?.response?.data?.detail || "").toLowerCase();
            if (detail.includes("expired")) toast.error(t("auth.reset_session_expired"));
            else toast.error(t("auth.reset_token_invalid"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="forgot-password-page" className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-6 md:p-16">
            <div className="w-full max-w-md space-y-8">
                <Link to="/login" className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground" data-testid="forgot-back-login">
                    <ArrowLeft className="h-4 w-4" />
                    {t("auth.back_to_login")}
                </Link>

                {step === "email" && (
                    <>
                        <div className="space-y-2">
                            <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight">
                                {t("auth.forgot_password_title")}
                            </h1>
                            <p className="text-muted-foreground font-medium">{t("auth.forgot_password_subtitle")}</p>
                        </div>
                        <form onSubmit={submitEmail} className="space-y-4" data-testid="forgot-email-form">
                            <div>
                                <label className="text-sm font-semibold block mb-1.5">{t("auth.email")}</label>
                                <input data-testid="forgot-email" type="email" required value={email}
                                    onChange={(e) => setEmail(e.target.value)} className="input-modern" placeholder="you@email.com" />
                            </div>
                            <button type="submit" data-testid="forgot-email-submit" disabled={busy} className="btn-primary w-full disabled:opacity-50">
                                {busy ? t("common.loading") : t("auth.send_code")}
                            </button>
                        </form>
                    </>
                )}

                {step === "code" && (
                    <>
                        <div className="space-y-2">
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted text-xs font-semibold">
                                <MailCheck className="h-3.5 w-3.5" />
                                {t("auth.code_sent_short")}
                            </div>
                            <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight">
                                {t("auth.enter_code_title")}
                            </h1>
                            <p className="text-muted-foreground font-medium">{t("auth.enter_code_subtitle", { email })}</p>
                        </div>
                        <form onSubmit={submitCode} className="space-y-6">
                            <CodeInput value={code} onChange={setCode} testIdPrefix="forgot-code" />
                            <button type="submit" data-testid="forgot-code-submit" disabled={busy || code.length !== 6} className="btn-primary w-full disabled:opacity-50">
                                {busy ? t("common.loading") : t("auth.verify")}
                            </button>
                        </form>
                    </>
                )}

                {step === "newpwd" && (
                    <>
                        <div className="space-y-2">
                            <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight">
                                {t("auth.new_password_title")}
                            </h1>
                            <p className="text-muted-foreground font-medium">{t("auth.new_password_subtitle")}</p>
                        </div>
                        <form onSubmit={submitNewPwd} className="space-y-4" data-testid="forgot-newpwd-form">
                            <div>
                                <label className="text-sm font-semibold block mb-1.5">{t("auth.new_password")}</label>
                                <div className="relative">
                                    <input data-testid="forgot-new-password" type={show ? "text" : "password"} required minLength={6}
                                        value={pwd} onChange={(e) => setPwd(e.target.value)} className="input-modern pr-12" />
                                    <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                                        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </button>
                                </div>
                            </div>
                            <div>
                                <label className="text-sm font-semibold block mb-1.5">{t("auth.confirm_password")}</label>
                                <input data-testid="forgot-confirm-password" type={show ? "text" : "password"} required minLength={6}
                                    value={pwd2} onChange={(e) => setPwd2(e.target.value)} className="input-modern" />
                            </div>
                            <button type="submit" data-testid="forgot-newpwd-submit" disabled={busy} className="btn-primary w-full disabled:opacity-50">
                                {busy ? t("common.loading") : t("auth.update_password")}
                            </button>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
}
